import styled from '@emotion/styled'
import { css } from '@emotion/react'
import TextField, { TextFieldProps } from '@mui/material/TextField'
import InputAdornment from '@mui/material/InputAdornment'
import IconButton from '@mui/material/IconButton'
import CloseIcon from './assets/remove.svg?react'
import { useState, forwardRef } from 'react'

const StyledTextField = styled(TextField)(
    props => css`
        color: rgba(0, 59, 111, 1);

        &.MuiFormControl-root {
            border-radius: 8px;
            background-color: #ffffff;
            border: ${props.disabled ? '1px solid rgba(226, 232, 240, 1)' : '1px solid rgba(204, 222, 239, 1)'} !important;

            & .MuiInputBase-input {
                font-family: var(--font-inter);
                font-size: 16px;
                font-weight: 400;
                color: rgba(0, 59, 111, 1);
                line-height: 20px;
                padding: 0px;
                transform: translate(14px, 10px);

                &::placeholder {
                    color: rgba(90, 143, 195, 1);
                    opacity: 1;
                }

                &.Mui-focused,
                &:focus {
                    border-bottom: 0px;
                    outline: none;
                }

                &.Mui-disabled {
                    color: rgba(56, 66, 80, 0.5) !important;
                }
            }
            &:hover {
                border: 1px solid rgba(204, 222, 239, 1) !important;
            }

            &:focus-within {
                outline: none;
                box-shadow: none;
            }

            .MuiInputLabel-root {
                font-size: 16px;
                font-family: var(--font-inter);
                color: rgba(90, 143, 195, 1);
                transform: translate(14px, 19px) scale(1);
                &.Mui-disabled {
                    &.MuiInputLabel-shrink {
                        color: rgba(56, 66, 80, 0.5) !important;
                        background-color: transparent !important;
                    }
                }
                &.MuiInputLabel-shrink {
                    color: rgba(90, 143, 195, 1);
                    background-color: #ffffff;
                    padding: 0 6px;
                    margin-left: -4px;
                    transform: translate(14px, 10px) scale(0.75);
                }
            }
        }

        & .MuiInputBase-root {
            border-radius: 8px;
            background-color: #ffffff !important;
            height: 60px;

            &::after {
                content: '';
                border-bottom: 0px !important;
            }
            &::before {
                content: '';
                border-bottom: 0px !important;
            }
            &.Mui-focused {
                border-bottom: 0px;
                outline: none;
            }
            &.no-shrink {
                & .MuiInputBase-input {
                    transform: translate(14px, 0px) scale(1);
                }
            }
            &.Mui-disabled {
                background-color: rgba(245, 245, 245, 1) !important;
                & .MuiInputBase-input {
                    color: rgba(56, 66, 80, 0.5) !important;
                }
            }
        }

        & .MuiOutlinedInput-notchedOutline {
            border: none;
        }

        & .MuiFormHelperText-root {
            font-family: var(--font-inter);
            font-size: 12px;
            margin-left: 0;
            margin-top: 4px;

            &.Mui-error {
                color: rgba(254, 93, 124, 1);
            }
        }

        &.MuiFormControl-root:has(.Mui-error) {
            border-color: rgba(254, 93, 124, 1) !important;
        }
    `
)

export interface UInputProps extends Omit<TextFieldProps, 'variant'> {
    onClear?: () => void
}

/**
 * UInput - Styled TextField component for GLIC2027
 * Based on U25 UInput pattern
 * Shows clear button when focused and has value
 */
export const UInput = forwardRef<HTMLDivElement, UInputProps>(({ onClear, onFocus, onBlur, value, slotProps, type = 'text', autoComplete, ...props }, ref) => {
    const [isFocused, setIsFocused] = useState(false)

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(true)
        onFocus?.(e)
    }

    const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        setIsFocused(false)
        onBlur?.(e)
    }

    const handleClear = (e: React.MouseEvent) => {
        e.preventDefault()
        onClear?.()
    }

    const showClearButton = onClear && isFocused && value && String(value).length > 0
    const existingEndAdornment = (slotProps?.input as any)?.endAdornment

    return (
        <StyledTextField
            ref={ref}
            type={type}
            autoComplete={autoComplete ?? (type === 'text' ? 'new-password' : undefined)}
            value={value}
            onFocus={handleFocus}
            onBlur={handleBlur}
            slotProps={{
                ...slotProps,
                input: {
                    ...slotProps?.input,
                    endAdornment: showClearButton ? (
                        <InputAdornment position="end">
                            <IconButton
                                disableFocusRipple
                                disableRipple
                                disableTouchRipple
                                size="small"
                                onMouseDown={handleClear}
                                sx={{ color: 'rgba(90, 143, 195, 1)' }}>
                                <CloseIcon />
                            </IconButton>
                        </InputAdornment>
                    ) : (
                        existingEndAdornment
                    )
                }
            }}
            {...props}
        />
    )
})

UInput.displayName = 'UInput'

export default UInput
